import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { MapPin, RotateCcw, Route as RouteIcon, Trash2 } from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import StatPill from '../../components/ui/StatPill';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Skeleton from '../../components/ui/Skeleton';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import { getTrash, restoreTrashItem, purgeTrashItem } from '../../utils/api';
import { relativeTime } from '../../utils/commandCenterHelpers';

export default function TrashPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [purgeItem, setPurgeItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTrash();
      const merged = [
        ...res.data.routes.map((r) => ({ ...r, entityType: 'route' })),
        ...res.data.stops.map((s) => ({ ...s, entityType: 'stop' }))
      ].sort((a, b) => new Date(b.deletedAt) - new Date(a.deletedAt));
      setItems(merged);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load trash');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function restore(item) {
    setBusyId(item.id);
    try {
      await restoreTrashItem(item.entityType, item.id);
      toast.success(`Restored "${item.name}"`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Restore failed');
    } finally {
      setBusyId(null);
    }
  }

  async function confirmPurge() {
    if (!purgeItem) return;
    setBusyId(purgeItem.id);
    try {
      await purgeTrashItem(purgeItem.entityType, purgeItem.id);
      toast.success('Permanently deleted');
      setPurgeItem(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Purge failed');
    } finally {
      setBusyId(null);
    }
  }

  const counts = useMemo(
    () => ({
      routes: items.filter((i) => i.entityType === 'route').length,
      stops: items.filter((i) => i.entityType === 'stop').length
    }),
    [items]
  );

  return (
    <>
      <PageHeader
        eyebrow="Super Admin"
        title="Trash"
        subtitle="Recover deleted stops and routes, or remove them permanently along with their history."
        icon={Trash2}
        pills={[
          <StatPill key="r" icon={RouteIcon} label="Routes" value={counts.routes} />,
          <StatPill key="s" icon={MapPin} label="Stops" value={counts.stops} />
        ]}
      />

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white shadow-soft">
        {loading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <span className="mx-auto mb-3 h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-500 inline-flex items-center justify-center">
              <Trash2 className="h-6 w-6" />
            </span>
            <p className="text-[14px] font-medium text-slate-700">Trash is empty</p>
            <p className="text-[12.5px] text-slate-500 mt-1">Deleted stops and routes will appear here for recovery.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((item) => (
              <li key={`${item.entityType}-${item.id}`} className="px-5 py-3.5 flex items-center gap-3">
                <span className="h-9 w-9 rounded-xl bg-slate-100 text-slate-500 inline-flex items-center justify-center shrink-0">
                  {item.entityType === 'route' ? <RouteIcon className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-[13.5px] font-semibold text-slate-900 truncate">{item.name}</p>
                    <Badge variant="outline" size="sm">{item.entityType === 'route' ? 'Route' : 'Stop'}</Badge>
                    {item.entityType === 'route' && (
                      <span className="text-[11.5px] text-slate-400">{item.stopCount} stops</span>
                    )}
                  </div>
                  <p className="text-[11.5px] text-slate-400">Deleted {relativeTime(item.deletedAt)} · ref {item.ref}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={RotateCcw}
                    loading={busyId === item.id}
                    onClick={() => restore(item)}
                  >
                    Restore
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={Trash2}
                    onClick={() => setPurgeItem(item)}
                    className="text-rose-600 hover:bg-rose-50"
                  >
                    Delete forever
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={!!purgeItem}
        onClose={() => setPurgeItem(null)}
        onConfirm={confirmPurge}
        loading={busyId === purgeItem?.id}
        title="Delete permanently"
        confirmLabel="Delete forever"
        description={purgeItem ? `"${purgeItem.name}" and its entire version history will be erased.` : ''}
      >
        <p className="text-[13px] text-slate-600">This cannot be undone. The record and all of its revision history will be removed.</p>
      </ConfirmDialog>
    </>
  );
}
